import { SearchProgress } from "@/components/PatientMrnSearch";
import { usePageTraffic } from "@/lib/usePageTraffic";
import AppChip from "@/components/AppChip";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ERSearch } from "@/components/encounter/ERSearch";
import { DOSV_CSS } from "@/components/encounter/DosVisit";
import { useSearchParams, useNavigate } from "react-router-dom";
import { X, ScanLine, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useCodingWatch } from "@/lib/useCodingWatch";
import { api } from "@/lib/api";
import { useClient } from "@/lib/clientContext";
import { searchNotes, signNote, getNote, needsPatientMatch, listNoteSplitBatches, listServiceLocations, loadNotesByAdded } from "@/lib/noteClaim";
import { listProviders } from "@/lib/dictation";
import { patientOf, payerOf } from "@/lib/claimReadiness";
import { useChartTabs } from "@/lib/chartTabs";
import { provOf, isSigned, isGenerated, sourceLabel } from "@/lib/createClaimsModel";
import { titleCaseNameWithCreds } from "@/lib/patientFormat";
import { LoadingOverlay } from "@/components/LoadPerfOverlay";
import { DateRangePicker } from "@/components/encounter/DateRangePicker";
import { BasisToggle } from "@/components/BasisToggle"; // eslint-disable-line no-unused-vars -- retired from toolbar; basis now lives in the date button menu
import { ERSortFilter } from "@/components/encounter/ERSortFilter";
import { ERRow } from "@/components/encounter/ERRow";
import { ERFooter } from "@/components/encounter/ERFooter";
import { ClaimRowsSkeleton } from "@/components/Skeletons";
import { BulkPayerHeader, NoPayerHeader } from "@/components/encounter/ERBulkPayer";
import { INK, INDIGO, LINK, SUB, FAINT, HEADFAINT, HAIR, PANEL, AMBER, SOFT, CLAIM_ST, ADV_FIELDS, QF, quickSearch, rangeDos, rangeFromTo, rangeLabel, ymd, addDays } from "@/components/encounter/erShared";

const SOURCES = [["all", "All", null], ["dictation", "Dictation", "dictation"], ["televisit", "Telehealth", "televisit"], ["document_upload", "Documents", "document_upload"], ["superbill_import", "Superbills", "superbill_import"], ["pasted_note", "Pasted notes", "pasted_note"]];
// [key, label, amberDot, hideWhenZero] — Codes out of date & No patient match are always shown as segmented filters (per user request), counts respect source/date/filter/search.
const SEGMENTS = [["unsigned", "Needs signature", true, false], ["stale", "Codes out of date", true, false], ["no_match", "No patient match", true, false], ["signed", "Signed", false, false], ["all", "All", false, false]];
const segFilter = (seg) => seg === "unsigned" ? { signed: false } : seg === "stale" ? { codes_stale: true } : seg === "signed" ? { signed: true } : {};
// Part F — client-side segment test, used when the Date-added basis loads the full set in one pass.
const segMatch = (n, seg) => seg === "unsigned" ? !isSigned(n) : seg === "signed" ? isSigned(n) : seg === "stale" ? !!n.codes_stale : seg === "no_match" ? needsPatientMatch(n) : true;
const NO_F = { group: "none", sort: "added", provider: "", tov: "", status: "", payer: "" };

const ER_CSS = `.rq-tg{display:inline-flex;align-items:center;height:36px;border:1px solid #E9E9EC;border-radius:10px;background:#fff}
.rq-tg>.rq-tg-part{display:inline-flex;align-items:center;height:36px;position:relative}
.rq-tg>.rq-tg-part+.rq-tg-part{border-left:1px solid #E9E9EC}
.rq-tg>.rq-tg-part:hover{background:#F6F6F8}
.er-chip{display:inline-flex;align-items:center;gap:5px;height:32px;padding:0 9px;border:1px solid #E9E9EC;border-radius:8px;background:#fff;color:#6C6C69}
.er-chip:hover{background:#F6F6F8}
.er-chip .dot{width:6px;height:6px;border-radius:999px;background:#5856D6;display:inline-block;animation:erPulse 1.4s ease-in-out infinite}
@keyframes erPulse{0%,100%{opacity:1}50%{opacity:.35}}
.er-seg-dot{width:6px;height:6px;border-radius:999px;display:inline-block;background:#FF9500}
.er-tovcell{display:inline-flex;align-items:center;gap:6px;height:28px;padding:0 8px;margin-left:-8px;border-radius:8px;background:transparent;cursor:pointer;font-size:13px;line-height:1;max-width:100%;transition:background-color .12s}
.er-tovcell .er-tovchev{width:12px;height:12px;flex:none;color:#6C6C69;opacity:0;transition:opacity .12s}
.er-row:hover .er-tovcell,.er-tovcell:focus-visible,.er-tovcell[data-open="true"]{background:#F1F1F4;outline:none}
.er-row:hover .er-tovcell .er-tovchev,.er-tovcell:focus-visible .er-tovchev,.er-tovcell[data-open="true"] .er-tovchev{opacity:1}
.er-dosfold .er-tovcell{padding:0 4px 0 6px;gap:2px;height:22px;margin-left:-6px;max-width:none}
.er-cp{position:relative;display:block;padding-right:26px}
.er-cpbtn{position:absolute;right:0;top:50%;transform:translateY(-50%);width:24px;height:24px;display:inline-flex;align-items:center;justify-content:center;border-radius:7px;background:#fff;color:#6C6C69;opacity:0;transition:opacity .12s,background-color .12s,color .12s}
.er-cp:hover .er-cpbtn,.er-cpbtn:focus-visible{opacity:1}
.er-cpbtn:hover{background:#F1F1F4;color:#141412}
@media (hover:none){.er-cpbtn{opacity:.6}}
.er-signbtn{height:28px;padding:0 12px;border-radius:8px;background:#fff;border:1px solid #E9E9EC;color:#141412;font-size:12.5px;font-weight:600;transition:background-color .12s,color .12s,border-color .12s}
.er-row:hover .er-signbtn:not(.er-review),.er-signbtn:not(.er-review):focus-visible{background:#141412;color:#fff;border-color:#141412;outline:none}
.er-srctab{position:relative;height:40px;display:inline-flex;align-items:center;gap:6px;flex:none;background:none;border:0;cursor:pointer;font-size:14px;font-weight:500;color:#6C6C69;white-space:nowrap;transition:color .12s}
.er-srctab:hover{color:#141412}
.er-srctab[aria-selected="true"]{color:#141412;font-weight:600;box-shadow:inset 0 -2px 0 #141412}
.er-qs{border:1px solid #E9E9EC;transition:border-color .12s,box-shadow .12s}
.er-qs:focus-within{border-color:#5856D6;box-shadow:0 0 0 3px rgba(88,86,214,.15)}
.er-qs-chip{background:#F1F1F4;color:#141412}.er-qs-chip:hover,.er-qs-chip[data-open]{background:#E8E8EC}
.er-chev{width:18px;height:18px;display:inline-grid;place-items:center;border-radius:5px}
.er-item{position:relative;background:#fff;border-bottom:1px solid #E9E9EC;transition:transform .16s cubic-bezier(.3,.7,.3,1),box-shadow .16s cubic-bezier(.3,.7,.3,1),filter .2s ease,opacity .2s ease}
.er-item:not(.open):hover{transform:translateY(-1px);box-shadow:0 0 0 1px #E9E9EC,0 6px 18px rgba(20,20,18,.09);z-index:2;border-bottom-color:transparent}
.er-item:has(+ .er-item:not(.open):hover){border-bottom-color:transparent}
.er-item.open{z-index:3;background:#FBFBFD}
.er-item.open::before{content:"";position:absolute;left:0;top:-1px;bottom:0;width:3px;background:#141412;z-index:4;pointer-events:none}
.er-list.focus .er-item:not(.open){filter:blur(1.6px);opacity:.5;cursor:pointer}
.er-list.focus .er-item:not(.open) *{pointer-events:none}
.er-list.focus .er-item:not(.open):hover{opacity:.9;transform:none;box-shadow:none}
@media (prefers-reduced-motion:reduce){.er-item:not(.open):hover{transform:none}.er-list.focus .er-item:not(.open),.er-list.focus .er-item:not(.open):hover{filter:none;opacity:.45}}
.er-load-ov{position:fixed;inset:0;z-index:60;display:flex;align-items:center;justify-content:center;background:rgba(249,249,251,.55);backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px);animation:erfade .2s ease}
@keyframes erfade{from{opacity:0}to{opacity:1}}
.er-load-card{width:min(440px,90vw);background:#fff;border:1px solid #E9E9EC;border-radius:16px;box-shadow:0 24px 64px -24px rgba(20,20,18,.34);padding:22px 24px}
.er-load-head{display:flex;align-items:center;gap:10px;margin-bottom:16px}
.er-load-spin{width:16px;height:16px;border-radius:50%;border:2px solid #E4E4F5;border-top-color:#5856D6;animation:erspin .7s linear infinite;flex:none}
@keyframes erspin{to{transform:rotate(360deg)}}
.er-load-title{font-size:14.5px;font-weight:700;color:#141412}
.er-load-timer{margin-left:auto;font-variant-numeric:tabular-nums;font-size:16px;font-weight:800;color:#5856D6}
.er-load-timer em{font-style:normal;font-size:10px;font-weight:700;color:#9B9B97;margin-left:2px}
.er-load-track{height:8px;border-radius:999px;background:#F1F1F4;overflow:hidden}
.er-load-fill{height:100%;border-radius:999px;background:linear-gradient(90deg,#5856D6,#8B8AF0,#5856D6);background-size:200% 100%;animation:ershim 1.1s linear infinite;transition:width .18s ease}
@keyframes ershim{to{background-position:-200% 0}}
.er-load-stages{display:flex;flex-wrap:wrap;gap:7px;margin-top:16px}
.er-load-chip{font-size:11px;font-weight:700;letter-spacing:.02em;color:#9B9B97;background:#F6F5F3;border:1px solid #EFEEEB;border-radius:999px;padding:4px 11px;transition:color .25s ease,background .25s ease,transform .25s ease,box-shadow .25s ease}
.er-load-chip.on{color:#fff;background:#5856D6;border-color:#5856D6;transform:translateY(-1px);box-shadow:0 4px 12px -4px rgba(88,86,214,.6)}
.er-loadms-badge{position:absolute;top:18px;right:28px;z-index:20;display:inline-flex;align-items:center;gap:6px;font-size:11.5px;font-weight:700;font-variant-numeric:tabular-nums;color:#4A4AC2;background:#ecebfb;border:1px solid #d7d5f5;border-radius:999px;padding:4px 11px}
.er-loadms-badge .dot{width:6px;height:6px;border-radius:50%;background:#5856D6}`;

const LOAD_STAGES = ["Patient", "ICD-10", "CPT", "Payer", "Charge", "Optimizing"];
function ERLoadingOverlay() {
  return <LoadingOverlay label="Loading encounters" stages={LOAD_STAGES} />;
}

// Part D §3 — column minimums (12px column gap, 22px row padding). Tiers 4→0 fold by the list's own width.
const CW = {
  patient: { min: 172, fr: 1.3 }, dos: { px: 152 }, tov: { min: 110, fr: 0.8 },
  source: { min: 90, fr: 0.6 }, loc: { min: 140, fr: 1 }, phys: { min: 150, fr: 1.1 },
  payer: { min: 124, fr: 0.8 }, coding: { min: 156, fr: 0.9 }, status: { px: 198 },
};
const HEADS = { patient: "Patient", dos: "DOS · Visit", tov: "Visit type", source: "Source", loc: "Location", phys: "Physician · Billing provider", payer: "Payer", coding: "Coding", status: "Status" };
const COL_ORDER = ["patient", "dos", "tov", "source", "loc", "phys", "payer", "coding", "status"];
// locMode: "off" | "multi" | "single" (single = single-location, switched on by hand → optional, tier 4 only).
function colKeysForTier(t, showSourceCol, locMode) {
  const keys = new Set(["patient", "dos", "phys", "status"]);
  if (t >= 2) keys.add("tov");            // visit type folds into DOS at tier 1
  if (t >= 1) keys.add("coding");          // coding stays until the row stacks
  if (t >= 3) keys.add("payer");           // payer folds into patient at tier ≤ 2
  if (t === 4 && showSourceCol) keys.add("source");
  if (locMode === "multi" && t >= 3) keys.add("loc");
  if (locMode === "single" && t === 4) keys.add("loc");
  return COL_ORDER.filter((k) => keys.has(k));
}
function layoutFor(w, showSourceCol, locMode) {
  for (let t = 4; t >= 1; t -= 1) {
    const cols = colKeysForTier(t, showSourceCol, locMode).map((k) => ({ k, ...CW[k], h: HEADS[k] }));
    const need = cols.reduce((a, c) => a + (c.px || c.min), 0) + 12 * (cols.length - 1) + 22;
    if (need <= w) return { tier: t, cols, grid: cols.map((c) => (c.px ? `${c.px}px` : `minmax(${c.min}px,${c.fr}fr)`)).join(" ") };
  }
  return { tier: 0, cols: [{ k: "patient", h: "Encounter" }, { k: "status", h: "Status" }], grid: "minmax(0,1fr) 198px" };
}
const ER_STATE = "revq.er.state";
const scrollerOf = (el) => { let e = el; while (e && e !== document.body) { const o = getComputedStyle(e).overflowY; if ((o === "auto" || o === "scroll") && e.scrollHeight > e.clientHeight) return e; e = e.parentElement; } return document.scrollingElement; };

function Chip({ label, val, onRemove, testid }) {
  return (
    <span className="inline-flex items-center gap-1 text-[12px] rounded-[6px] px-2 h-[24px]" style={{ background: SOFT, color: INK }} data-testid={testid}>
      <span style={{ color: SUB }}>{label}:</span> {val}
      <button onClick={onRemove} aria-label={`Remove ${label}`} data-testid={`${testid}-rm`}><X size={11} /></button>
    </span>
  );
}

function groupLabel(n, g) {
  if (g === "physician") return { key: provOf(n) || "—", text: provOf(n) ? titleCaseNameWithCreds(provOf(n)) : "No physician" };
  if (g === "billing") {
    const br = n.billing_relationship;
    if (br?.status === "missing" || !br?.billing_name) return { key: "__missing", text: "Billing provider missing", amber: true };
    return { key: br.billing_id || br.billing_name, text: br.billing_name };
  }
  if (g === "patient") return { key: patientOf(n) || "—", text: titleCaseNameWithCreds(patientOf(n)) || "—" };
  return { key: sourceLabel(n), text: sourceLabel(n) };
}

export default function EncounterReview() {
  const { selectedClientId } = useClient();
  const navigate = useNavigate();
  const { openChart, openNote, openClaim: openClaimTab, openMpu } = useChartTabs();
  const [params] = useSearchParams();
  const urlSource = params.get("source");
  // Deep link from Telehealth Activity: ?note=<id>[&visit=VIS-…] → All, searched by visit ID, row open.
  const urlNote = params.get("note"), urlVisit = params.get("visit") || "";
  // ‹ Encounter review from the claim workspace restores the page as it was left.
  const [restored] = useState(() => { if (params.get("restore") !== "1") return null; try { return JSON.parse(sessionStorage.getItem(ER_STATE) || "null"); } catch { return null; } });

  const [source, setSource] = useState(restored?.source || (SOURCES.some((s) => s[0] === urlSource) ? urlSource : "all"));
  const [seg, setSeg] = useState(restored?.seg || (urlNote ? "all" : "unsigned"));
  const [range, setRange] = useState(restored?.range || { kind: "preset", key: "d7" });
  // True only when we auto-widened an empty last-7-days view to last-30 — drives the explanatory nudge.
  const [auto30, setAuto30] = useState("");
  const [basis, setBasis] = useState(restored?.basis || "added");
  // Auto basis fallback: default to "date added"; if that window has no results, fall back to
  // "date of service" (and vice-versa). One cycle per context; skipped once a basis is chosen by hand.
  const basisPinned = useRef(!!restored?.basis);
  const basisTries = useRef(0);
  const pickBasis = (b) => {
    basisPinned.current = true;
    // Give the newly chosen basis its own fresh auto-widen evaluation: the one-shot is per-client,
    // so without this a widen already spent in one basis would block the nudge in the other.
    didAuto30.current = false;
    if (auto30) { setRange({ kind: "preset", key: "d7" }); setAuto30(""); }
    setBasis(b);
    setOpenId(null);
  };
  const [f, setF] = useState(restored?.f || NO_F);
  const [adv, setAdv] = useState(restored?.adv || {});
  const [searchText, setSearchText] = useState(restored?.searchText ?? urlVisit);
  const [forced, setForced] = useState(restored?.forced || null);
  const [qsDebounced, setQsDebounced] = useState(null);
  const [sfOpen, setSfOpenRaw] = useState(false);
  const [sfTab, setSfTab] = useState("filter");
  const [svcLocs, setSvcLocs] = useState([]);
  const [locOverride, setLocOverride] = useState(null); // null = automatic, true/false = set by hand (session)
  const [srcCol, setSrcCol] = useState(false);          // "Source as its own column" (All tab only)
  const [size, setSize] = useState(50);
  const [pageState, setPageState] = useState({ key: "", page: 1 });

  const [rows, setRows] = useState([]);
  const fresh = useCodingWatch(rows, setRows);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState(null); // total call→screen ms of the last load (shown top-right)
  const [openId, setOpenId] = useState(restored?.openId || urlNote);
  const [counts, setCounts] = useState({});
  const [srcMeta, setSrcMeta] = useState({});
  const [moreIn30, setMoreIn30] = useState(0);
  const [addedN, setAddedN] = useState(0);
  const [addedHasOlder, setAddedHasOlder] = useState(false);
  const [providers, setProviders] = useState([]);
  const [tovList, setTovList] = useState([]);
  const [payers, setPayers] = useState([]);
  const { report: reportTraffic, markSearch: markSearchTraffic } = usePageTraffic("encounter-review");
  const erFirst = useRef(true);
  const perfRef = useRef(null); // timing: { t0 (call start), t1 (data in), count } → paint measured in effect
  const [nonce, setNonce] = useState(0);
  const [mpuFailed, setMpuFailed] = useState(0);
  const [insFlowId, setInsFlowId] = useState(null);
  const [needsIdAll, setNeedsIdAll] = useState([]);
  const bump = () => setNonce((n) => n + 1);
  // Amber count on the Multi-part uploads tab entry = parts still to review across batches.
  useEffect(() => {
    if (!selectedClientId) return undefined;
    let alive = true;
    const load = () => listNoteSplitBatches({ client_id: selectedClientId, limit: 50 })
      .then((b) => { if (alive) setMpuFailed((b || []).reduce((s, x) => s + (x.failed_count ?? (x.segments || []).filter((g) => g.status !== "created").length), 0)); })
      .catch(() => {});
    load();
    const iv = setInterval(() => { if (!document.hidden) load(); }, 20000);
    return () => { alive = false; clearInterval(iv); };
  }, [selectedClientId, nonce]);
  // Open visit → from the Multi-part uploads tab: land here with that visit expanded (status All so it's in view).
  useEffect(() => {
    let id = null, q = null;
    try { id = sessionStorage.getItem("revq.er.openVisit"); q = sessionStorage.getItem("revq.er.search"); } catch { /* ignore */ }
    if (id) { try { sessionStorage.removeItem("revq.er.openVisit"); } catch { /* ignore */ } setSeg("all"); setSource("all"); setOpenId(id); }
    else if (q) { try { sessionStorage.removeItem("revq.er.search"); } catch { /* ignore */ } setSeg("all"); setSource("all"); setSearchText(q); }
  }, []);
  // After a note save: refresh just that row (and the counts) instead of reloading the whole list.
  const [quiet, setQuiet] = useState(0);
  const refreshRow = useRef(null);
  refreshRow.current = async (id) => {
    const n = await getNote(id).catch(() => null);
    if (n) { setRows((all) => all.map((x) => (x.id === id ? { ...x, ...n } : x))); setPinned((p) => (p && p.id === id ? { ...p, ...n } : p)); }
    setQuiet((q) => q + 1);
  };
  const setSfOpen = useCallback((v) => { setSfOpenRaw(v); if (!v) setSfTab("filter"); }, []);

  useEffect(() => {
    const onExpand = (e) => { const id = e.detail?.noteId; if (id) { setOpenId(id); refreshRow.current(id); } };
    const onSaved = (e) => { const id = e.detail?.noteId; if (id) refreshRow.current(id); else bump(); };
    window.addEventListener("revq:expand-encounter", onExpand);
    window.addEventListener("revq:note-saved", onSaved);
    return () => { window.removeEventListener("revq:expand-encounter", onExpand); window.removeEventListener("revq:note-saved", onSaved); };
  }, []);

  const qs = useMemo(() => quickSearch(searchText, forced), [searchText, forced]);
  const qsField = qs ? (qs.field ? JSON.stringify(qs.field) : "pending") : "";
  // Not valid yet → keep the list as it was; valid → one field, page 1.
  useEffect(() => { if (qsField === "pending") return undefined; const t = setTimeout(() => setQsDebounced(qsField ? JSON.parse(qsField) : null), 300); return () => clearTimeout(t); }, [qsField]);

  // Every control on the page except segment + paging.
  const filterBody = useMemo(() => ({
    client_id: String(selectedClientId),
    ...(source !== "all" ? { source: SOURCES.find((s) => s[0] === source)[2] } : {}),
    // A patient/MRN search looks across all dates (visits with no DOS included), like Claims Queue.
    // Part F — the Date-added basis never sends a DOS window; it pages by created_at instead.
    ...(adv.patient || adv.mrn || qsDebounced || basis === "added" ? {} : rangeDos(range)),
    ...(f.provider ? { provider: f.provider } : {}),
    ...(f.tov ? { tov: f.tov } : {}),
    ...(f.status ? { status: f.status } : {}),
    ...(f.payer && f.payer !== "__needs_id__" ? { payer: f.payer } : {}),
    ...adv,
    ...(qsDebounced || {}),
  }), [selectedClientId, source, range, basis, f.provider, f.tov, f.status, f.payer, qsDebounced, adv]);

  // Part F — a field search (patient/MRN/quick-search) overrides the date basis and spans all dates.
  const searching = !!(adv.patient || adv.mrn || qsDebounced);
  const useAdded = basis === "added" && !searching;
  // Filters passed to the added-basis loader (no segment, no date — so one fetch serves every segment count).
  const addedExtra = useMemo(() => ({
    client_id: String(selectedClientId),
    ...(source !== "all" ? { source: SOURCES.find((s) => s[0] === source)[2] } : {}),
    ...(f.provider ? { provider: f.provider } : {}),
    ...(f.tov ? { tov: f.tov } : {}),
    ...(f.status ? { status: f.status } : {}),
    ...(f.payer && f.payer !== "__needs_id__" ? { payer: f.payer } : {}),
  }), [selectedClientId, source, f.provider, f.tov, f.status, f.payer]);

  const pageKey = JSON.stringify([filterBody, seg, size, basis, range]);
  const page = pageState.key === pageKey ? pageState.page : 1;
  const setPage = (p) => setPageState({ key: pageKey, page: p });

  useEffect(() => {
    if (!selectedClientId) return;
    listProviders().then(setProviders).catch(() => setProviders([]));
    api.get("/api/edi/note-to-claim/clients").then((r) => {
      const c = (r.data?.clients || r.data || []).find((x) => String(x.client_id) === String(selectedClientId));
      setTovList(c?.tov_list || []);
    }).catch(() => setTovList([]));
    api.get(`/api/edi/data-migration/config/payers?client_id=${encodeURIComponent(selectedClientId)}`)
      .then((r) => setPayers([...new Set((r.data?.records || []).map((p) => p.name).filter(Boolean))].sort()))
      .catch(() => setPayers([]));
  }, [selectedClientId]);

  // Part E — service locations per client (one call, cached for the session). Decides multi-location.
  useEffect(() => {
    setLocOverride(null); setSrcCol(false);
    if (!selectedClientId) { setSvcLocs([]); return undefined; }
    let alive = true;
    listServiceLocations(selectedClientId).then((l) => { if (alive) setSvcLocs(l || []); }).catch(() => { if (alive) setSvcLocs([]); });
    return () => { alive = false; };
  }, [selectedClientId]);

  useEffect(() => {
    if (!selectedClientId) return;
    let alive = true;
    (async () => {
      const meta = {};
      await Promise.all(SOURCES.filter((s) => s[2]).map(async ([key, , val]) => {
        const [ex, un] = await Promise.all([
          searchNotes({ client_id: String(selectedClientId), source: val, limit: 1 }).then((r) => r.total).catch(() => 0),
          searchNotes({ client_id: String(selectedClientId), source: val, signed: false, limit: 1 }).then((r) => r.total).catch(() => 0),
        ]);
        meta[key] = { exists: ex > 0, unsigned: un };
      }));
      if (alive) setSrcMeta(meta);
    })();
    return () => { alive = false; };
  }, [selectedClientId, nonce]);

  const load = useCallback(async () => {
    if (!selectedClientId) { setRows([]); setTotal(0); setCounts({}); setLoading(false); return; }
    if (!erFirst.current) markSearchTraffic({ ...filterBody, segment: seg, page });
    setLoading(true);
    const perfT0 = performance.now();
    const perfLabel = useAdded ? "added-basis" : `search seg=${seg} page=${page}`;
    console.log(`[ER perf] ① API call started — ${perfLabel}`);
    try {
      if (useAdded) {
        // Page by date added; derive the page, the total and every segment count from one full fetch.
        const { rows: all, hasOlder } = await loadNotesByAdded(addedExtra, rangeFromTo(range));
        const perfT1 = performance.now();
        setAddedHasOlder(hasOlder);
        setCounts({ unsigned: all.filter((n) => !isSigned(n)).length, stale: all.filter((n) => !!n.codes_stale).length, no_match: all.filter(needsPatientMatch).length, signed: all.filter((n) => isSigned(n)).length, all: all.length });
        const segged = all.filter((n) => segMatch(n, seg));
        setTotal(segged.length);
        setRows(segged.slice((page - 1) * size, page * size));
        console.log(`[ER perf] ② API data received in ${(perfT1 - perfT0).toFixed(0)}ms — ${all.length} notes fetched, ${segged.length} after segment filter`);
        perfRef.current = { t0: perfT0, t1: perfT1, count: Math.min(size, segged.length - (page - 1) * size), label: perfLabel };
        if (erFirst.current) erFirst.current = false;
        reportTraffic(segged.length);
      } else {
        const r = await searchNotes({ ...filterBody, ...segFilter(seg), skip: (page - 1) * size, limit: size });
        const perfT1 = performance.now();
        setRows(r.notes); setTotal(r.total); setAddedHasOlder(false);
        console.log(`[ER perf] ② API data received in ${(perfT1 - perfT0).toFixed(0)}ms — ${r.notes.length} rows (${r.total} total)`);
        perfRef.current = { t0: perfT0, t1: perfT1, count: r.notes.length, label: perfLabel };
        if (erFirst.current) erFirst.current = false;
        reportTraffic(r.total);
      }
    } catch { setRows([]); setTotal(0); } finally { setLoading(false); }
  }, [selectedClientId, filterBody, seg, page, size, useAdded, addedExtra, range, reportTraffic, markSearchTraffic]);

  useEffect(() => { load(); }, [load, nonce]);

  // Timing: measure when the fetched rows are actually painted (two rAFs = after the browser paints).
  useEffect(() => {
    const p = perfRef.current;
    if (!p) return;
    perfRef.current = null;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const t2 = performance.now();
      setLoadMs(t2 - p.t0);
      console.log(`[ER perf] ③ ${p.count} rows on screen — render+paint ${(t2 - p.t1).toFixed(0)}ms after data · TOTAL call→screen ${(t2 - p.t0).toFixed(0)}ms (${p.label})`);
    }));
  }, [rows]);

  // Client-wide "needs payer id" set (independent of the active segment/filters) for the bulk pill.
  useEffect(() => {
    if (!selectedClientId) { setNeedsIdAll([]); return undefined; }
    let alive = true;
    // GET /notes lists the whole client (searchNotes rejects a client-only query with 400).
    api.get("/api/edi/note-to-claim/notes", { params: { client_id: String(selectedClientId), limit: 100 } })
      .then((r) => { if (alive) setNeedsIdAll((r.data?.notes || []).filter((n) => !payerOf(n) || insNeedsId(n.insurance))); })
      .catch(() => { if (alive) setNeedsIdAll([]); });
    return () => { alive = false; };
  }, [selectedClientId, nonce, quiet]);

  useEffect(() => {
    if (!selectedClientId || useAdded) return;   // Part F — added basis sets counts inside load().
    let alive = true;
    Promise.all(SEGMENTS.filter(([k]) => k !== "no_match").map(([k]) => searchNotes({ ...filterBody, ...segFilter(k), limit: 1 }).then((r) => [k, r.total]).catch(() => [k, 0])))
      .then((pairs) => { if (alive) setCounts(Object.fromEntries(pairs)); });
    return () => { alive = false; };
  }, [selectedClientId, filterBody, nonce, quiet, useAdded]);

  // Count in-scope encounters within the last 30 days but older than the last 7 — drives both the
  // "N more in the last 30 days" nudge and the empty-7-day auto-widen. Runs in both date bases.
  useEffect(() => {
    if (!selectedClientId || (seg !== "unsigned" && seg !== "stale") || searching) { setMoreIn30(0); return undefined; }
    const d30From = ymd(addDays(new Date(), -29));
    const olderTo = ymd(addDays(new Date(), -7));
    let alive = true;
    if (basis === "dos") {
      const { dos_from, dos_to, ...rest } = filterBody;
      searchNotes({ ...rest, ...segFilter(seg), dos_from: d30From, dos_to: olderTo, limit: 1 })
        .then((r) => { if (alive) setMoreIn30(r.total || 0); }).catch(() => { if (alive) setMoreIn30(0); });
    } else {
      loadNotesByAdded({ ...addedExtra, ...segFilter(seg) }, { from: d30From, to: olderTo })
        .then(({ rows: more }) => { if (alive) setMoreIn30(more.length); }).catch(() => { if (alive) setMoreIn30(0); });
    }
    return () => { alive = false; };
  }, [selectedClientId, basis, seg, filterBody, addedExtra, searching, nonce, quiet]);

  // Part F — safety net: while filtering by DOS, count visits added inside the range whose DOS falls outside it.
  useEffect(() => {
    if (!selectedClientId || basis !== "dos" || searching || range.kind === "before") { setAddedN(0); return undefined; }
    const { from, to } = rangeFromTo(range);
    let alive = true;
    loadNotesByAdded({ ...addedExtra, ...segFilter(seg) }, { from, to }).then(({ rows: added }) => {
      if (!alive) return;
      const inRange = (iso) => { const c = (iso || "").slice(0, 10); return !!c && (!from || c >= from) && (!to || c <= to); };
      setAddedN(added.filter((n) => !inRange(n.date_of_service)).length);
    }).catch(() => { if (alive) setAddedN(0); });
    return () => { alive = false; };
  }, [selectedClientId, basis, range, seg, addedExtra, searching, nonce, quiet]);

  // Default to last 7 days, but if that window is empty while older unsigned/stale encounters exist
  // (common for tenants whose data predates the last week), widen to last 30 days automatically — once per client.
  const didAuto30 = useRef(false);
  useEffect(() => { didAuto30.current = false; setAuto30(""); }, [selectedClientId]);
  // Reset the basis fallback whenever the client (new context) or the filter/range context changes.
  useEffect(() => { basisPinned.current = !!restored?.basis; }, [selectedClientId]);
  useEffect(() => { basisTries.current = 0; }, [selectedClientId, source, seg, addedExtra, adv, range, qsDebounced]);
  // If the chosen date basis yields nothing, fall back to the other basis automatically.
  useEffect(() => {
    if (loading || basisPinned.current || searching || f.payer === "__needs_id__") return;
    if (total === 0 && basisTries.current < 2) {
      basisTries.current += 1;
      setBasis((b) => (b === "added" ? "dos" : "added"));
      setOpenId(null);
    }
  }, [loading, total, searching, f.payer]);
  useEffect(() => {
    if (loading || didAuto30.current || restored) return;
    if ((seg === "unsigned" || seg === "stale") && range.kind === "preset" && range.key === "d7" && rows.length === 0 && (moreIn30 > 0 || addedHasOlder) && (basisPinned.current || basisTries.current >= 2)) {
      didAuto30.current = true;
      setRange({ kind: "preset", key: "d30" });
      setAuto30("empty");
      setOpenId(null);
    }
  }, [loading, rows.length, moreIn30, addedHasOlder, seg, range, restored, selectedClientId]);

  // The deep-linked note is read directly and pinned on top, whatever the filters, until another row is opened.
  const [pinned, setPinned] = useState(null);
  useEffect(() => { if (!urlNote) { setPinned(null); return; } setOpenId(urlNote); getNote(urlNote).then((n) => setPinned(n && String(n.client_id) === String(selectedClientId) ? n : null)).catch(() => setPinned(null)); }, [urlNote, selectedClientId, nonce]);
  const insNeedsId = (ins) => !!(ins?.payer_name && (ins.needs_review || ins.payer_id_missing || !ins.payer_id || ins.payer_id === "MISSING"));
  const needsIdLocal = (n) => !payerOf(n) || insNeedsId(n.insurance);
  const noMatchView = seg === "no_match";
  const noMatchCount = useMemo(() => rows.filter(needsPatientMatch).length, [rows]);
  // G6 — "Needs payer or payer id" view: client-wide, grouped by carrier (bigger first), No-payer last. Ignores the date range.
  const needsIdView = f.payer === "__needs_id__";
  const needsIdGroups = useMemo(() => {
    if (!needsIdView) return null;
    const byCarrier = new Map(); const noPayer = [];
    needsIdAll.forEach((n) => {
      const carrier = payerOf(n);
      if (!carrier) { noPayer.push(n); return; }
      if (!byCarrier.has(carrier)) byCarrier.set(carrier, []);
      byCarrier.get(carrier).push(n);
    });
    const carriers = [...byCarrier.entries()].map(([carrier, notes]) => ({ carrier, notes })).sort((a, b) => b.notes.length - a.notes.length);
    return { carriers, noPayer };
  }, [needsIdView, needsIdAll]);

  // Part E — multi-location detection + short site names.
  const facName = (x) => x?.facility_name || x?.name || "";
  const facPos = (x) => String(x?.pos_code ?? x?.code ?? "");
  const locFacilities = useMemo(() => {
    const fromApi = svcLocs.filter((x) => facPos(x) !== "10").map(facName).filter(Boolean);
    if (fromApi.length) return [...new Set(fromApi)];
    const fromRows = rows.map((r) => (String(r.service_location?.code ?? "") !== "10" ? (r.service_location?.facility_name || "") : "")).filter(Boolean);
    return [...new Set(fromRows)];
  }, [svcLocs, rows]);
  const multiLoc = locFacilities.length > 1;
  const nLocations = locFacilities.length;
  const sharedPrefix = useMemo(() => {
    if (locFacilities.length < 2) return "";
    const toks = locFacilities.map((n) => n.split(/\s+/));
    let i = 0;
    for (;;) { const w = toks[0][i]; if (w == null || !toks.every((t) => t[i] === w)) break; i += 1; }
    return i === 0 ? "" : toks[0].slice(0, i).join(" ");
  }, [locFacilities]);
  const shortSite = useCallback((sl) => {
    if (!sl) return "";
    if (String(sl.code ?? "") === "10") return "Telehealth";
    const full = sl.facility_name || sl.name || "";
    if (!full) return "";
    if (sharedPrefix && full.startsWith(sharedPrefix)) { const s = full.slice(sharedPrefix.length).replace(/^[\s·\u2013\-,]+/, "").trim(); if (s) return s; }
    return full;
  }, [sharedPrefix]);
  const locOn = locOverride == null ? multiLoc : locOverride;
  const locMode = !locOn ? "off" : multiLoc ? "multi" : "single";

  const sorted = useMemo(() => {
    const base0 = pinned && openId === pinned.id ? [pinned, ...rows.filter((n) => n.id !== pinned.id)] : rows;
    let base = noMatchView ? base0.filter(needsPatientMatch) : base0;
    if (f.payer === "__needs_id__") base = base.filter(needsIdLocal);
    const dos = (n) => { const x = Date.parse(n.date_of_service || ""); return isNaN(x) ? 0 : x; };
    const added = (n) => { const x = Date.parse(n.created_at || ""); return isNaN(x) ? 0 : x; };
    if (f.sort === "wait") return [...base].sort((a, b) => added(a) - added(b));
    if (f.sort === "dos_new") return [...base].sort((a, b) => dos(b) - dos(a));
    if (f.sort === "dos_old") return [...base].sort((a, b) => dos(a) - dos(b));
    if (f.sort === "added") return [...base].sort((a, b) => added(b) - added(a) || dos(b) - dos(a));
    return base;
  }, [rows, f.sort, f.payer, pinned, openId, noMatchView]);

  const groups = useMemo(() => {
    if (f.group === "none") return null;
    const m = new Map();
    sorted.forEach((n) => { const g = groupLabel(n, f.group); if (!m.has(g.key)) m.set(g.key, { ...g, rows: [] }); m.get(g.key).rows.push(n); });
    return [...m.values()];
  }, [sorted, f.group]);

  const visibleSources = SOURCES.filter((s) => s[0] === "all" || srcMeta[s[0]]?.exists);
  const showPills = SOURCES.filter((s) => s[2] && srcMeta[s[0]]?.exists).length > 1;
  const showSource = source === "all";
  const showSourceCol = showSource && srcCol;
  const listRef = useRef(null);
  const [listW, setListW] = useState(1400);
  useEffect(() => {
    const el = listRef.current; if (!el) return undefined;
    let t; const ro = new ResizeObserver(() => { clearTimeout(t); t = setTimeout(() => setListW(el.clientWidth), 60); });
    ro.observe(el); setListW(el.clientWidth);
    return () => { ro.disconnect(); clearTimeout(t); };
  }, []);
  const lay = useMemo(() => layoutFor(listW, showSourceCol, locMode), [listW, showSourceCol, locMode]);
  const colSet = useMemo(() => new Set(lay.cols.map((c) => c.k)), [lay]);
  const grid = lay.grid;

  const physName = (npi) => providers.find((p) => p.npi_number === npi)?.provider_name || providers.find((p) => p.npi_number === npi)?.name || npi;
  const filterChips = [
    f.provider && { k: "provider", label: "Physician", val: titleCaseNameWithCreds(physName(f.provider)) },
    f.tov && { k: "tov", label: "Type of visit", val: f.tov },
    f.status && { k: "status", label: "Claim status", val: CLAIM_ST.find((s) => s[0] === f.status)?.[1] },
    f.payer && { k: "payer", label: "Payer", val: f.payer === "__needs_id__" ? "Insurance to fix · all dates" : f.payer },
  ].filter(Boolean);
  const advChips = Object.entries(adv).map(([k, v]) => ({ k, label: ADV_FIELDS.find((x) => x[0] === k)?.[1] || k, val: v }));
  const activeCount = filterChips.length + advChips.length + (f.group !== "none" ? 1 : 0) + (f.sort !== "added" ? 1 : 0);
  const clearFilters = () => setF((s) => ({ ...NO_F, group: s.group }));
  const removeAdv = (k) => setAdv((s) => { const { [k]: _, ...rest } = s; return rest; });

  const opts = {
    physicians: providers.map((p) => [p.npi_number, titleCaseNameWithCreds(p.provider_name || p.name || p.npi_number)]),
    tovs: tovList.map((t) => [t, t]),
    payers: payers.map((p) => [p, p]),
  };
  // Per-facet option counts, computed from the loaded rows with every OTHER active filter applied (not the facet's own).
  const normNm = (s) => String(s || "").toLowerCase().replace(/\s+/g, " ").trim();
  const nameToNpi = useMemo(() => { const m = new Map(); providers.forEach((p) => m.set(normNm(p.provider_name || p.name), p.npi_number)); return m; }, [providers]);
  const rowNpi = useCallback((n) => nameToNpi.get(normNm(provOf(n))) || "", [nameToNpi]);
  const rowStatusKey = (n) => (n.status === "processing" ? "processing" : n.status === "queued" ? "queued" : isGenerated(n) ? "generated" : "ready");
  const countFor = useCallback((facet, value) => {
    if (facet === "payer" && value === "__needs_id__") return needsIdAll.length;
    const passExcept = (n) => {
      if (facet !== "provider" && f.provider && rowNpi(n) !== f.provider) return false;
      if (facet !== "tov" && f.tov && (n.tov || "") !== f.tov) return false;
      if (facet !== "status" && f.status && rowStatusKey(n) !== f.status) return false;
      if (facet !== "payer" && f.payer) { if (f.payer === "__needs_id__" ? !needsIdLocal(n) : payerOf(n) !== f.payer) return false; }
      return true;
    };
    const base = rows.filter(passExcept);
    if (!value) return base.length;
    const pred = facet === "provider" ? (n) => rowNpi(n) === value
      : facet === "tov" ? (n) => (n.tov || "") === value
      : facet === "status" ? (n) => rowStatusKey(n) === value
      : (n) => payerOf(n) === value;
    return base.filter(pred).length;
  }, [rows, f.provider, f.tov, f.status, f.payer, needsIdAll, rowNpi]); // eslint-disable-line react-hooks/exhaustive-deps

  const openClaim = (n) => {
    try { sessionStorage.setItem(ER_STATE, JSON.stringify({ source, seg, range, basis, f, adv, searchText, forced, openId, scroll: scrollerOf(listRef.current)?.scrollTop || 0 })); } catch { /* storage blocked */ }
    // Open the claim tab straight from here (no hop through Claims Queue); the pager walks this list.
    if (openClaimTab) {
      const list = groups ? groups.flatMap((g) => g.rows) : sorted;
      const slim = (x) => ({ id: x.id, name: patientOf(x), patient_name: x.patient_name || "", date_of_service: x.date_of_service || null, claim_readiness: x.claim_readiness || null, visit_id: x.visit_id || "", mrn: x.mrn || "" });
      openClaimTab({ items: list.map(slim), index: Math.max(0, list.findIndex((x) => x.id === n.id)), railTab: "note", backLabel: "Encounter review" });
      return;
    }
    navigate(`/create-claims?claim=${encodeURIComponent(n.id)}&client=${encodeURIComponent(selectedClientId)}&tab=note&from=er`);
  };
  useEffect(() => {
    if (!restored?.scroll || loading) return;
    const t = setTimeout(() => { const sc = scrollerOf(listRef.current); if (sc) sc.scrollTop = restored.scroll; }, 50);
    return () => clearTimeout(t);
  }, [restored, loading]);
  // A click on a blurred row switches to it in one click; Esc collapses (focus back to the chevron).
  const switchTo = (n) => { setOpenId(n.id); setTimeout(() => document.querySelector(`[data-testid="er-row-${n.id}"]`)?.scrollIntoView({ block: "nearest" }), 60); };
  useEffect(() => {
    if (!openId) return undefined;
    const h = (e) => {
      if (e.key !== "Escape" || /^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement?.tagName || "") || document.activeElement?.isContentEditable) return;
      if (document.querySelector("[role=menu],[role=listbox],[role=dialog],[data-radix-popper-content-wrapper]")) return;
      const id = openId; setOpenId(null);
      setTimeout(() => document.querySelector(`[data-testid="er-chev-${id}"]`)?.focus(), 0);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [openId]);
  const signRow = async (n) => { try { await signNote(n.id); toast.success("Encounter signed."); bump(); } catch (e) { toast.error(e?.response?.data?.detail || "Could not sign."); } };
  const applyTov = (id, tovVal) => setRows((rs) => rs.map((r) => (r.id === id ? { ...r, tov: tovVal } : r)));
  const onOpenChart = (n) => { if (openChart) openChart({ uuid: n.chart_uuid, name: patientOf(n) }); else toast("This visit isn't linked to a chart."); };
  const onEdit = (n) => {
    if (isSigned(n)) { toast("Signed notes are locked. Unsign to edit, then sign again."); return; }
    if (openNote) openNote({ noteId: n.id, name: patientOf(n), chartUuid: n.chart_uuid, fromLabel: "Encounter review" });
  };
  const onOpenChartDocs = (n) => { if (openChart && n.chart_uuid) openChart({ uuid: n.chart_uuid, name: patientOf(n), intent: "documents" }); else toast("This visit isn't linked to a chart."); };
  const expandedProps = { onChanged: bump, onOpenClaim: openClaim, onEdit, onOpenChart, onOpenChartDocs, clientId: selectedClientId, openInsFor: insFlowId, onInsClose: () => setInsFlowId(null) };

  const rLabel = adv.patient || adv.mrn ? "All dates" : rangeLabel(range);
  const footerRange = searching ? rLabel : `${basis === "dos" ? "DOS" : "Added"} · ${rLabel}`;
  const addedRangeText = range.kind === "preset" ? (range.key === "today" ? "today" : `in the ${rangeLabel(range).toLowerCase()}`) : range.kind === "custom" ? `on ${rLabel}` : rLabel.toLowerCase();
  const sourceText = source === "all" ? "All sources" : SOURCES.find((s) => s[0] === source)[1];

  const focusOn = openId && sorted.some((n) => n.id === openId) ? openId : null;
  const [erSearchOpen, setErSearchOpen] = useState(false);
  const renderRow = (n) => (
    <ERRow key={n.id} n={n} grid={grid} cols={colSet} tier={lay.tier} showSource={showSource} locMode={locMode} shortSite={shortSite} open={openId === n.id} blurred={!!focusOn && focusOn !== n.id} onSwitch={switchTo} onToggle={() => { setInsFlowId(null); setOpenId(openId === n.id ? null : n.id); }} fresh={fresh.has(n.id)}
      tovList={tovList} onApplyTov={applyTov} onSign={signRow} onManageIns={(x) => { setInsFlowId(x.id); setOpenId(x.id); }} expandedProps={expandedProps} />
  );

  return (
    <div className="w-full mx-auto px-7 py-6 min-w-0 relative" data-testid="encounter-review-page">
      <style>{ER_CSS + DOSV_CSS}</style>
      {loading ? <ERLoadingOverlay /> : null}
      {/* Multi-part uploads now opens as a tab (Part B) — no lightbox */}
      <div className="mb-4">
        <div className="flex items-center gap-[10px] flex-wrap">
          <h1 className="u-page" data-testid="er-title">Encounter review</h1>
          <AppChip app="lumina" />
        </div>
        <p className="text-[13px] mt-1" style={{ color: SUB }}>Read it, fix it, sign it. Every visit you captured, whatever the source.</p>
      </div>

      {showPills ? (
        <div role="tablist" aria-label="Source" className="flex items-center mb-4 overflow-x-auto" style={{ gap: "24px", borderBottom: `1px solid ${HAIR}` }} data-testid="er-source-tabs">
          {visibleSources.map(([key, label]) => {
            const sel = source === key, un = key !== "all" ? (srcMeta[key]?.unsigned || 0) : 0;
            return (
              <button key={key} role="tab" aria-selected={sel} onClick={() => { setSource(key); setOpenId(null); }} data-testid={`er-tab-${key}`} className="er-srctab">
                {label}{un > 0 ? <span className="tabular-nums" style={{ fontSize: "12.5px", fontWeight: 600, color: sel ? SUB : FAINT }}>{un}</span> : null}
              </button>
            );
          })}
        </div>
      ) : null}

      <div className="rounded-[16px] min-w-0" style={{ background: "#fff", border: `1px solid ${HAIR}` }} data-testid="er-card">
        <div className="flex items-center gap-2 px-4 py-3 flex-wrap" style={{ borderBottom: `1px solid ${HAIR}` }}>
          <div className="inline-flex items-center rounded-[10px] p-[3px]" style={{ background: SOFT }} data-testid="er-segments">
            {SEGMENTS.map(([k, t, amber, hideZero]) => {
              const sel = seg === k, c = k === "no_match" ? (counts.no_match ?? noMatchCount) : counts[k];
              if (hideZero && !sel && !c) return null;
              return (
                <button key={k} onClick={() => { setSeg(k); setOpenId(null); }} data-testid={`er-seg-${k}`}
                  title={k === "no_match" ? "Visits revQ couldn't match to a patient on the roster" : undefined}
                  className="inline-flex items-center gap-1.5 text-[13px] font-semibold px-3 py-[6px] rounded-[7px]"
                  style={{ background: sel ? "#fff" : "transparent", color: sel ? INK : SUB, boxShadow: sel ? "0 1px 2px rgba(20,20,18,.1)" : "none" }}>
                  {amber ? <span className="er-seg-dot" /> : null}{t}
                  {c != null ? <span className="text-[11.5px] font-bold tabular-nums" style={{ color: c ? INK : FAINT }} data-testid={`er-seg-count-${k}`}>{c}</span> : null}
                </button>
              );
            })}
          </div>

          {mpuFailed > 0 ? (
            <button type="button" onClick={() => openMpu && openMpu({ backLabel: "Encounter review" })} data-testid="er-mpu-chip" className="er-chip flex-none" title={`Multi-part uploads · ${mpuFailed} to review`} aria-label={`Multi-part uploads · ${mpuFailed} to review`}>
              <ScanLine size={14} className="flex-none" />
              <span className="tabular-nums text-[12px] font-bold" style={{ color: "#9A5A00" }}>{mpuFailed}</span>
            </button>
          ) : null}

          <div className="flex items-center gap-2 min-w-0" style={{ flex: "1 1 320px", justifyContent: "flex-end", flexWrap: "nowrap" }} data-testid="er-toolbar-right">
            <div className="rq-tg" data-testid="er-toolgroup">
              <div className="rq-tg-part"><DateRangePicker range={range} basis={basis} onBasisChange={pickBasis} onChange={(r) => { setRange(r); setAuto30(""); setOpenId(null); }} inGroup /></div>
              <div className="rq-tg-part"><ERSearch text={searchText} setText={(v) => { setSearchText(v); setOpenId(null); }} forced={forced} setForced={setForced} collapsible expanded={erSearchOpen} onExpandedChange={setErSearchOpen} inGroup /></div>
              <div className="rq-tg-part"><ERSortFilter open={sfOpen} setOpen={setSfOpen} tab={sfTab} setTab={setSfTab} f={f} setF={setF} opts={opts} total={total}
                onClearFilters={clearFilters} adv={adv} onSearch={setAdv} activeCount={activeCount} countFor={countFor} inGroup
                more={{ locOverride, setLocOverride, multiLoc, nLocations, srcCol, setSrcCol, sourceAll: showSource }} /></div>
            </div>
          </div>
        </div>

        {advChips.length ? (
          <div className="flex items-center gap-2 flex-wrap px-4 py-2" style={{ borderBottom: `1px solid ${HAIR}` }} data-testid="er-search-results">
            <span className="text-[13px] font-bold" style={{ color: INK }}>Search results</span>
            <span className="text-[12.5px]" style={{ color: SUB }} data-testid="er-search-results-count">{total} encounter{total === 1 ? "" : "s"}</span>
            {advChips.map((c) => <Chip key={c.k} label={c.label} val={c.val} onRemove={() => removeAdv(c.k)} testid={`er-adv-chip-${c.k}`} />)}
            <button onClick={() => { setSfTab("search"); setSfOpenRaw(true); }} className="text-[12.5px] font-semibold" style={{ color: INDIGO }} data-testid="er-search-edit">Edit search</button>
            <button onClick={() => setAdv({})} className="text-[12.5px] font-semibold" style={{ color: INDIGO }} data-testid="er-search-clear">Clear</button>
          </div>
        ) : null}

        {filterChips.length ? (
          <div className="flex items-center gap-1.5 flex-wrap px-4 py-2" style={{ borderBottom: `1px solid ${HAIR}` }} data-testid="er-chips">
            <span className="text-[12px] mr-1" style={{ color: FAINT }}>Filtered by</span>
            {filterChips.map((c) => <Chip key={c.k} label={c.label} val={c.val} onRemove={() => setF((s) => ({ ...s, [c.k]: "" }))} testid={`er-chip-${c.k}`} />)}
            <button onClick={clearFilters} className="text-[12px] font-semibold ml-1" style={{ color: INDIGO }} data-testid="er-chips-clear">Clear</button>
          </div>
        ) : null}

        {auto30 && range.kind === "preset" && range.key === "d30" && !needsIdView ? (
          <div className="flex items-center gap-x-3 gap-y-2 flex-wrap" style={{ margin: "12px 14px", borderRadius: 10, background: PANEL, border: `1px solid ${SOFT}`, padding: "9px 12px" }} data-testid="er-auto30-notice">
            <span className="flex items-center gap-2 min-w-0">
              <span style={{ width: 8, height: 8, borderRadius: 999, background: INDIGO, flex: "none", display: "inline-block" }} />
              <span className="text-[13px]" style={{ color: INK }}>{auto30 === "empty" ? `Nothing in the last 7 days — showing the last 30${total ? ` (${total})` : ""}.` : `Showing the last 30 days${total ? ` (${total})` : ""}.`}</span>
              <button onClick={() => { setRange({ kind: "preset", key: "d7" }); setAuto30(""); setOpenId(null); }} style={{ color: LINK, fontWeight: 600, fontSize: 13 }} data-testid="er-auto30-back">Back to 7 days</button>
            </span>
          </div>
        ) : null}

        {((moreIn30 > 0 && range.kind === "preset" && range.key === "d7" && rows.length > 0) || addedN > 0) && !needsIdView ? (
          <div className="flex items-center gap-x-3 gap-y-2 flex-wrap" style={{ margin: "12px 14px", borderRadius: 10, background: PANEL, border: `1px solid ${SOFT}`, padding: "9px 12px" }} data-testid="er-notices">
            {addedN > 0 ? (
              <span className="flex items-center gap-2 min-w-0" data-testid="er-added-notice">
                <span style={{ width: 8, height: 8, borderRadius: 999, background: "#FF9500", flex: "none", display: "inline-block" }} />
                <span className="text-[13px]" style={{ color: INK }}>{addedN} visit{addedN === 1 ? " was" : "s were"} added {addedRangeText} with an older date of service</span>
                <button onClick={() => { pickBasis("added"); }} style={{ color: LINK, fontWeight: 600, fontSize: 13 }} data-testid="er-added-show">Show them</button>
              </span>
            ) : null}
            {moreIn30 > 0 && range.kind === "preset" && range.key === "d7" && rows.length > 0 && addedN > 0 ? <span style={{ width: 1, height: 16, background: HAIR, flex: "none", display: "inline-block" }} /> : null}
            {moreIn30 > 0 && range.kind === "preset" && range.key === "d7" && rows.length > 0 ? (
              <span className="flex items-center gap-2 min-w-0" data-testid="er-older">
                <span style={{ width: 8, height: 8, borderRadius: 999, background: "#FF9500", flex: "none", display: "inline-block" }} />
                <span className="text-[13px]" style={{ color: INK }}>{moreIn30} more in the last 30 days</span>
                <button onClick={() => { setRange({ kind: "preset", key: "d30" }); setAuto30("more"); setOpenId(null); }} style={{ color: LINK, fontWeight: 600, fontSize: 13 }} data-testid="er-older-show">Show last 30 days</button>
              </span>
            ) : null}
          </div>
        ) : null}

        <SearchProgress active={loading && sorted.length > 0} testid="er-search-progress" />
        <div ref={listRef} className="min-w-0" data-testid="er-table-scroll" data-tier={lay.tier} style={{ opacity: loading && sorted.length > 0 ? 0.55 : 1, transition: "opacity .2s" }}>
          <div className={`er-list ${focusOn ? "focus" : ""}`}>
            <div className="grid items-center gap-x-3 text-[12px] font-semibold" style={{ gridTemplateColumns: grid, color: HEADFAINT, borderBottom: `1px solid ${HAIR}`, background: PANEL, padding: "9px 11px" }} data-testid="er-head">
              {lay.cols.map((c) => <span key={c.k} className={`truncate ${c.k === "patient" && lay.tier ? "pl-[24px]" : ""}`}>{c.k === "tov" && showSource && !colSet.has("source") ? "Visit type · Source" : c.h}</span>)}
            </div>
            {needsIdView ? (
              (needsIdGroups.carriers.length === 0 && needsIdGroups.noPayer.length === 0) ? (
                <div className="py-12 text-center" data-testid="er-empty">
                  <div className="text-[14px] font-bold" style={{ color: INK }}>No visits need a payer id.</div>
                  <div className="text-[13px] mt-1" style={{ color: FAINT }}>Every visit has a payer with an id.</div>
                </div>
              ) : (
                <>
                  {needsIdGroups.carriers.map((g) => (
                    <React.Fragment key={g.carrier}>
                      <BulkPayerHeader carrier={g.carrier} notes={g.notes} clientId={selectedClientId} onApplied={bump} />
                      {g.notes.map(renderRow)}
                    </React.Fragment>
                  ))}
                  {needsIdGroups.noPayer.length ? (
                    <>
                      <NoPayerHeader notes={needsIdGroups.noPayer} />
                      {needsIdGroups.noPayer.map(renderRow)}
                    </>
                  ) : null}
                </>
              )
            ) : loading && !sorted.length ? (
              <ClaimRowsSkeleton />
            ) : sorted.length === 0 ? (
              <div className="py-12 text-center" data-testid="er-empty">
                {addedN > 0 && basis === "dos" && !searching ? (
                  <>
                    <div className="text-[14px] font-bold" style={{ color: INK }}>No visits with a date of service {rLabel.toLowerCase()}.</div>
                    <div className="text-[13px] mt-1" style={{ color: FAINT }}>{addedN} {addedN === 1 ? "was" : "were"} added recently with older dates. <button onClick={() => { pickBasis("added"); }} className="font-semibold" style={{ color: LINK }} data-testid="er-empty-show-added">Show them</button></div>
                  </>
                ) : (
                  <>
                    <div className="text-[14px] font-bold" style={{ color: INK }}>{qsDebounced && qs?.ok ? `No encounters match ${QF[qs.key][0]} "${searchText.trim()}".` : noMatchView ? "Every visit here is matched to a patient." : seg === "unsigned" && !activeCount ? "You're all signed." : "Nothing in this view."}</div>
                    <div className="text-[13px] mt-1" style={{ color: FAINT }}>{qsDebounced && qs?.ok ? "Check the spelling, pick a different field from the search chip, or widen the date range." : noMatchView ? "Nothing here needs a patient match." : adv.patient || adv.mrn ? "No match in this tab. Check the other tabs above, or All." : seg === "unsigned" && !activeCount ? "Nothing here is waiting on you." : "Try a wider date range or clear the filters."}</div>
                  </>
                )}
              </div>
            ) : groups ? groups.map((g) => (
              <React.Fragment key={g.key}>
                <div className="flex items-center gap-2 px-4 py-2" style={{ background: PANEL, borderBottom: `1px solid ${HAIR}` }} data-testid={`er-group-${g.key}`}>
                  <span className="text-[13px] font-bold" style={{ color: g.amber ? AMBER : INK }}>{g.text}</span>
                  <span className="text-[12px]" style={{ color: FAINT }}>{g.rows.length} on this page</span>
                </div>
                {g.rows.map(renderRow)}
              </React.Fragment>
            )) : sorted.map(renderRow)}
          </div>
        </div>

        {!needsIdView ? <ERFooter page={page} size={size} total={total} rangeText={footerRange} sourceText={sourceText} waiting={f.sort === "wait"} loadMs={loadMs} onPage={(p) => { setPage(p); setOpenId(null); }} onSize={setSize} /> : null}
      </div>
    </div>
  );
}
